/**
 * src/features/pro-module/data/leaseGames.js
 * Content database for Module 10: The Contractual Ambiguity Audit.
 * Updated with "Ink Stain" Failure Logic.
 */

export const LEASE_GAMES = {
  'first-apartment-lease': {
    id: 'game_lease_1',
    moduleId: 'renter-module',
    title: "Contractual Ambiguity Audit",
    totalErrors: 3,
    
    // 1. THE BRIEFING
    preGameBriefing: {
        title: "The Fine Print",
        script: "You are about to audit a legally binding contract. Malicious actors frequently embed 'Gotcha Clauses' into standard boilerplate agreements. The Mission: Spot the 3 illegal clauses before executing the agreement. WARNING: You have a limited 'Mistake Budget'. If you flag too many legal clauses, you demonstrate poor ambiguity tolerance and the deal will collapse.",
        videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
    },

    // 2. FAILURE LOGIC
    failureLogic: {
        'voided_contract': {
            title: "Offer Rescinded",
            script: "You demonstrated poor ambiguity tolerance. You flagged perfectly legal standard clauses as 'Illegal.' Precision matters when auditing legal frameworks.",
            videoUrl: null
        },
        'missed_liability': {
            title: "Joint & Several Liability",
            script: "Stop! You missed the 'Roommate Responsibility' clause. Legal translation: 'Joint and Several Liability' means if your roommate skips town, YOU owe 100% of their rent. Never sign this without a side agreement.",
            videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
        },
        'missed_privacy': {
            title: "The Privacy Trap",
            script: "You let them keep the right to enter your home 'at any time'. In most states, that's illegal. You must demand 24-hour notice. Don't let them turn your apartment into a fishbowl.",
            videoUrl: null
        },
        'timeout': {
            title: "Contract Signed",
            script: "Time's up. You just signed a lease with illegal clauses. In the real world, you are now locked in for 12 months. Speed and accuracy are your only defense.",
            videoUrl: null
        }
    },

    // 3. GAME CONTENT
    leaseText: [
      { id: 'l1', text: 'LEASE AGREEMENT: This residential lease is for a period of 12 months, commencing on the 1st of the month.' },
      { 
        id: 'trap_entry', 
        text: 'The Landlord reserves the right to enter the unit at any time without prior notice for any reason including general inspection.', 
        isError: true, 
        feedback: 'Illegal Clause (Privacy): NJ and most states require 24-hour notice except in true emergencies.' 
      },
      { id: 'l2', text: 'Monthly rent shall be paid via the online portal. A late fee of $50 applies after the 5th.' },
      { 
        id: 'trap_deposit', 
        text: 'The Security Deposit of $1,500 is strictly non-refundable regardless of the unit condition and will be kept as a cleaning fee.', 
        isError: true, 
        feedback: 'Illegal Clause (Theft): Deposits are for damages, not guaranteed fees. State law mandates they are refundable minus itemized repairs.' 
      },
      { id: 'l3', text: 'Tenant is responsible for keeping the unit in a clean and sanitary condition.' },
      { 
        id: 'trap_repairs', 
        text: 'Tenant assumes all responsibility for structural repairs, roof maintenance, and main water line leaks occurring during the term.', 
        isError: true, 
        feedback: 'Illegal Clause (Liability): Landlords are legally responsible for habitability and major structural systems.' 
      },
      { id: 'l4', text: 'This represents the entire agreement between the parties.' },
      { id: 'l5', text: 'Quiet hours are enforced between 10:00 PM and 7:00 AM daily.' },
      { id: 'l6', text: 'Subletting is permitted only with written consent from the Landlord.' }
    ],

    briefing: {
      title: 'Operation: Contractual Ambiguity Audit',
      subtitle: "Don't sign your rights away.",
      steps: [
        'Scan the document for "Illegal Clauses".',
        'Tap to "Red-Line" (Strike-out) the traps.',
        'Find all 3 to secure your deposit.'
      ],
      tips: 'State law beats whatever a landlord puts on paper.'
    }
  },

  // --- NEW: CORPORATE LUXURY LEASE ---
  'luxury-building-traps': {
    id: 'game_lease_luxury',
    moduleId: 'renter-module',
    title: "The 'Luxury' Complex",
    totalErrors: 4,
    preGameBriefing: {
        title: "Death by a Thousand Fees",
        script: "You're moving into a corporate 'Luxury' building. They use algorithmic pricing and aggressive addendums to bleed you dry. Find the 4 predatory corporate clauses.",
        videoUrl: null
    },
    failureLogic: {
        'voided_contract': { title: "Lease Denied", script: "You flagged standard utilities. The corporate landlord marked you as 'Combative' and leased to someone else." },
        'missed_hike': { title: "The Auto-Hike", script: "You missed the variable rent clause! Next year, they can raise your rent by 40% with zero notice. You are legally trapped." },
        'timeout': { title: "Signed Blind", script: "Time's up. You signed it. Prepare to pay $100 every time you leave a trash bag in the hallway." }
    },
    leaseText: [
      { id: 'l1', text: 'Base rent is $2,200/month. Access to the fitness center and pool is included.' },
      { 
        id: 'trap_variable_fees', 
        text: 'Management reserves the right to introduce new monthly "Amenity Fees" at any time with 5 days notice.', 
        isError: true, 
        feedback: 'Variable Fee Trap: Your rent and mandatory fees must be locked for the duration of the lease term.' 
      },
      { 
        id: 'trap_auto_renew', 
        text: 'Unless Tenant provides 90 days written notice, this lease will automatically renew for 12 months at the algorithmic "Market Rate" (up to a 300% increase).', 
        isError: true, 
        feedback: 'Predatory Auto-Renew: Forcing 90-day notice with an uncapped variable rent increase is a classic corporate trap.' 
      },
      { id: 'l2', text: 'Trash valet service is mandatory at $35/month.' },
      { 
        id: 'trap_hvac', 
        text: 'Tenant accepts the unit "As-Is" and is financially responsible for replacing the HVAC unit if it breaks during the lease term.', 
        isError: true, 
        feedback: 'Illegal Transfer of Liability: Renter cannot be held responsible for major capital expenditures like HVAC systems.' 
      },
      { 
        id: 'trap_gag_clause', 
        text: 'Tenant agrees not to post negative reviews of the property on Google, Yelp, or TikTok under penalty of a $500 fine.', 
        isError: true, 
        feedback: 'Illegal Gag Clause: The Consumer Review Fairness Act voids any contract contract clause that threatens penalties for honest reviews.' 
      }
    ]
  },

  // --- NEW: THE SHADY SUBLET ---
  'shady-sublet': {
    id: 'game_lease_sublet',
    moduleId: 'renter-module',
    title: "The Sketchy Sublet",
    totalErrors: 3,
    preGameBriefing: {
        title: "The Handshake Deal",
        script: "You found a cheap room on Facebook Marketplace. The primary tenant hands you a 'custom' sublet agreement they wrote themselves. Beware: Unofficial sublets are where the most predatory traps hide. Find the 3 illegal traps.",
        videoUrl: null
    },
    failureLogic: {
        'voided_contract': { title: "Kicked Out", script: "You complained about standard roommate chores and got kicked out before moving in." },
        'timeout': { title: "Signed Blind", script: "Time's up. You signed. Good luck getting your deposit back in crypto." }
    },
    leaseText: [
      { id: 'l1', text: 'Month-to-month sublet agreement. Rent is due on the 1st.' },
      { 
        id: 'trap_crypto', 
        text: 'All rent payments must be made in Bitcoin or untraceable cash via mail.', 
        isError: true, 
        feedback: 'Illegal Trap: You have no paper trail. You can never prove you paid rent.' 
      },
      { id: 'l2', text: 'Tenant gets one shelf in the refrigerator and shared bathroom access.' },
      { 
        id: 'trap_eviction', 
        text: 'If the Landlord decides they need the room back, the Tenant agrees to vacate within 24 hours.', 
        isError: true, 
        feedback: 'Illegal Eviction: Even month-to-month tenants are legally entitled to 30 days written notice.' 
      },
      { id: 'l3', text: 'Utilities are split evenly among all roommates.' },
      { 
        id: 'trap_guests', 
        text: 'No guests are allowed on the property at any time, for any reason. Overnight or otherwise.', 
        isError: true, 
        feedback: 'Illegal Trap (Quiet Enjoyment): Landlords cannot ban you from having normal guests in a space you pay for.' 
      }
    ]
  }
};